import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Clock, Trophy, type LucideIcon } from 'lucide-react';

import {
  RECRUITMENT_STAGE_ORDER,
  type CourierMondayStatus,
  type RecruitmentStage,
} from 'features/courier-portal/services/courierPortalService';

interface Props {
  status: CourierMondayStatus | null;
  loading: boolean;
}

const STAGE_LABEL_KEY: Record<RecruitmentStage, string> = {
  DOCUMENTS: 'portal.stage.documents',
  TRAINING: 'portal.stage.training',
  CONTRACT: 'portal.stage.contract',
  EQUIPMENT: 'portal.stage.equipment',
  APPROVED: 'portal.stage.approved',
  STUCK: 'portal.stage.stuck',
  INCOMPLETE_PROCESS: 'portal.stage.incompleteProcess',
  DISQUALIFIED: 'portal.stage.disqualified',
};

const SummaryMetric: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: LucideIcon;
}> = ({ label, value, icon: Icon }) => (
  <div className="min-w-0 p-4 sm:p-5">
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold truncate">
        {label}
      </span>
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#bf0035]/[0.06] text-[#bf0035]">
        <Icon size={14} />
      </div>
    </div>
    <p className="text-xl sm:text-2xl font-extrabold text-gray-900 truncate">
      {value}
    </p>
  </div>
);

const SkeletonCard: React.FC = () => (
  <div className="mb-4 animate-pulse rounded-xl border border-gray-200 bg-white">
    <div className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {[0, 1, 2].map((item) => (
        <div key={item} className="space-y-3 p-5">
          <div className="h-3 w-20 rounded bg-gray-100" />
          <div className="h-6 w-24 rounded bg-gray-100" />
        </div>
      ))}
    </div>
    <div className="mx-5 mb-5 h-2 rounded bg-gray-100" />
  </div>
);

const RecruitmentKPIs: React.FC<Props> = ({ status, loading }) => {
  const { t } = useTranslation();
  const total = RECRUITMENT_STAGE_ORDER.length;

  if (loading && !status) {
    return <SkeletonCard />;
  }

  if (!status) return null;

  const stageOrder = status.stageOrder;
  const approvedIndex = RECRUITMENT_STAGE_ORDER.indexOf('APPROVED');
  const completed =
    stageOrder >= approvedIndex
      ? total
      : stageOrder >= 0
        ? Math.min(stageOrder, total)
        : 0;
  const progressPct =
    stageOrder >= 0 ? Math.round((completed / total) * 100) : 0;
  const currentStage =
    stageOrder >= 0
      ? RECRUITMENT_STAGE_ORDER[Math.min(stageOrder, total - 1)]
      : null;
  const stageLabel = currentStage ? t(STAGE_LABEL_KEY[currentStage]) : '—';

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      <SummaryMetric
        label={t('portal.kpi.currentStage')}
        value={stageLabel}
        icon={Clock}
      />
      <SummaryMetric
        label={t('portal.kpi.completed')}
        value={t('portal.kpi.outOf', { done: completed, total })}
        icon={Sparkles}
      />
      <SummaryMetric
        label={t('portal.kpi.progress')}
        value={`${progressPct}%`}
        icon={Trophy}
      />
      </div>
      <div className="border-t border-gray-100 p-4 sm:px-5">
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-gray-100"
          role="progressbar"
          aria-label={String(t('portal.kpi.progress'))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPct}
        >
          <div
            className="h-full rounded-full bg-[#bf0035] transition-[width] duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default RecruitmentKPIs;
